import { achievementDefinitions } from "./achievementDefinitions";
import type { AchievementId } from "./types";
import type { HovAmplifierEntryId } from "@/features/hoverIdle/hov/hovAmplifierModel";
import {
  DEFAULT_UPGRADE_REPEAT_INITIAL_INTERVAL_MS,
  HOV_AMPLIFIER_UPGRADE_REPEAT_INITIAL_INTERVAL_MS,
  HOV_AMPLIFIER_UPGRADE_UNLOCKED_REPEAT_INITIAL_INTERVAL_MS,
  HOV_GAIN_FAMILY_UPGRADE_UNLOCKED_REPEAT_INITIAL_INTERVAL_MS,
  HOV_GAIN_FAMILY_UPGRADE_MARATHON_REPEATER_INITIAL_INTERVAL_MS,
} from "@/features/hoverIdle/upgrades/upgradeRepeatTiming";
import { huge, hugeOne, type HugeNumber } from "@/shared/numbers";

export interface AchievementPersistentEffects {
  readonly hovGainFlatBonus: number;
  readonly hovUpgradeCostMultiplier: number;
  readonly hovCellUpgradeCostMultiplier: number;
  readonly hovGainFamilyUpgradeRepeatInitialIntervalMs: number;
  // Amplifier upgrade purchase timing only. This does not unlock MPM placement
  // on Amplifier panels, which remains owned by the Hov skill effect.
  readonly amplifierUpgradeRepeatInitialIntervalMs: number;
  readonly hovGainAchievementCountMultiplier: number;
  readonly timGainAchievementCountMultiplier: number;
  readonly hovGainAchievementMultiplier: number;
  readonly mpCapacityBonus: number;
  readonly mpmPresetSlotBonus: number;
  readonly canAssignMpToHovGainUpgrades: boolean;
  // Amplifier upgrade purchase actions only. Amp panel/gauge MP placement is
  // owned separately by the Hov skill effect.
  readonly canAssignMpToAmplifierUpgrades: boolean;
  readonly timHovActivationEfficiency: number;
  readonly bhgToHgPowerExponentBonus: number;
  // Additive exponent bonus applied only to the H1G cell's effective Hov gain
  // (post-pipeline, pre-cost). Does not propagate to H2G..H9G or HnG.
  readonly h1gExponentBonus: number;
  // When true, the hover repeater path collapses runs of 1-cost upgrade
  // purchases into a single tick.
  readonly oneCostHoverRepeaterBatchEnabled: boolean;
  // When true, the OHov HovCore reward multiplier from
  // `ohov-hov-core-gain-multiplier-bonus` scales with the current OHov power
  // rather than staying at its base value.
  readonly ohovHovCoreGainScalingUnlocked: boolean;
  // When true, the MHov reward HG multiplier is no longer capped at the
  // Number-limit boundary (1.8e308) and grows as a HugeNumber. Granted by the
  // `outside-mhov` achievement.
  readonly mhovRewardUncapped: boolean;
  // When true, every NHov branch effect is no longer capped at the
  // Number-limit boundary (1.8e308) and grows as a HugeNumber. Granted by the
  // `outside-nhov` achievement.
  readonly nhovBranchEffectUncapped: boolean;
  readonly explorationTimAccelerationUnlocked: boolean;
  readonly amplifierGainFixedMultipliersByEntry: ReadonlyMap<
    HovAmplifierEntryId,
    HugeNumber
  >;
}

export interface AchievementOneTimeEffect {
  readonly tokenGrant?: number;
}

interface AchievementEffectEntry {
  readonly persistent?: Partial<
    Pick<
      AchievementPersistentEffects,
      | "hovGainFlatBonus"
      | "hovUpgradeCostMultiplier"
      | "hovCellUpgradeCostMultiplier"
      | "hovGainFamilyUpgradeRepeatInitialIntervalMs"
      | "amplifierUpgradeRepeatInitialIntervalMs"
      | "hovGainAchievementMultiplier"
      | "mpCapacityBonus"
      | "mpmPresetSlotBonus"
      | "canAssignMpToHovGainUpgrades"
      | "canAssignMpToAmplifierUpgrades"
      | "timHovActivationEfficiency"
      | "bhgToHgPowerExponentBonus"
      | "h1gExponentBonus"
      | "oneCostHoverRepeaterBatchEnabled"
      | "ohovHovCoreGainScalingUnlocked"
      | "mhovRewardUncapped"
      | "nhovBranchEffectUncapped"
      | "explorationTimAccelerationUnlocked"
      | "amplifierGainFixedMultipliersByEntry"
    >
  >;
  readonly oneTime?: AchievementOneTimeEffect;
}

export const HOV_GAIN_ACHIEVEMENT_COUNT_BASE_DEFAULT = 1.01;
export const TIM_GAIN_ACHIEVEMENT_COUNT_BASE_DEFAULT = 1.01;

export const ACHIEVEMENT_SECRET_TOKEN_GRANT = 100;

export const SECOND_MHOV_HG_MULTIPLIER = 1.5;

export const NEW_NEW_HOV_HG_MULTIPLIER = 2;

export const FOURTH_HOV_GAIN_MP_BONUS = 1;
export const NINTH_HOV_GAIN_MP_BONUS = 2;
export const MASS_HOV_GAIN_FAMILY_UPGRADE_MP_BONUS = 1;
export const MUSIC_POINTER_MP_BONUS = 1;
export const MOVIE_POINTER_MP_BONUS = 1;
export const MASCHINEN_POINTER_MP_BONUS = 1;
export const MHOV_MP_EXECUTION_TEN_MP_BONUS = 1;
export const MP_WALL_PRESET_SLOT_BONUS = 1;

export const CHEAP_FOURTH_HOV_GAIN_HG_HNG_COST_DIVISOR = 2;
export const CHEAP_GLOBAL_HOV_GAIN_MILESTONE_HOV_GAIN_FAMILY_COST_DIVISOR = 2;
export const NOT_FULLY_FREE_HG_HNG_COST_DIVISOR = 1.01;

export const CHEAP_FOURTH_HOV_GAIN_COST_MULTIPLIER =
  1 / CHEAP_FOURTH_HOV_GAIN_HG_HNG_COST_DIVISOR;

export const CHEAP_GLOBAL_HOV_GAIN_MILESTONE_COST_MULTIPLIER =
  1 / CHEAP_GLOBAL_HOV_GAIN_MILESTONE_HOV_GAIN_FAMILY_COST_DIVISOR;

export const NOT_FULLY_FREE_COST_MULTIPLIER =
  1 / NOT_FULLY_FREE_HG_HNG_COST_DIVISOR;

export const TIM_HOV_ACTIVATION_EFFICIENCY_DEFAULT = 1;
export const TIM_HOV_ACTIVATION_EFFICIENCY_AFTER_TIME_HAS_COME = 100;

export const GOOGLE_HG_POWER_EXPONENT_BONUS = 0.01;
export const GLOBAL_HOV_GAIN_UPGRADE_TENTH_MILESTONE_HG_POWER_EXPONENT_BONUS = 0.01;
export const MASSIVE_MERGE_HOV_HG_POWER_EXPONENT_BONUS = 0.01;
export const RANK_GAP_HG_POWER_EXPONENT_BONUS = 0.01;
export const TENTH_OUTSIDE_HOV_HG_POWER_EXPONENT_BONUS = 0.01;

export const HOV_1_GAIN_TRIES_TOO_H1G_EXPONENT_BONUS = 0.01;
export const HOV_1_GAIN_GETS_SERIOUS_H1G_EXPONENT_BONUS = 0.1;

export const RED_AMPLIFIER_FULL_CHARGE_RAG_MULTIPLIER = 1.1;
export const EXPLORATION_BIOME_LEVEL_MP_BONUS = 1;

export const NEUTRAL_ACHIEVEMENT_PERSISTENT_EFFECTS: AchievementPersistentEffects =
  {
    hovGainFlatBonus: 0,
    hovUpgradeCostMultiplier: 1,
    hovCellUpgradeCostMultiplier: 1,
    hovGainFamilyUpgradeRepeatInitialIntervalMs:
      DEFAULT_UPGRADE_REPEAT_INITIAL_INTERVAL_MS,
    amplifierUpgradeRepeatInitialIntervalMs:
      HOV_AMPLIFIER_UPGRADE_REPEAT_INITIAL_INTERVAL_MS,
    hovGainAchievementCountMultiplier: 1,
    timGainAchievementCountMultiplier: 1,
    hovGainAchievementMultiplier: 1,
    mpCapacityBonus: 0,
    mpmPresetSlotBonus: 0,
    canAssignMpToHovGainUpgrades: false,
    canAssignMpToAmplifierUpgrades: false,
    timHovActivationEfficiency: TIM_HOV_ACTIVATION_EFFICIENCY_DEFAULT,
    bhgToHgPowerExponentBonus: 0,
    h1gExponentBonus: 0,
    oneCostHoverRepeaterBatchEnabled: false,
    ohovHovCoreGainScalingUnlocked: false,
    mhovRewardUncapped: false,
    nhovBranchEffectUncapped: false,
    explorationTimAccelerationUnlocked: false,
    amplifierGainFixedMultipliersByEntry: new Map(),
  };

const ACHIEVEMENT_COUNT_MULTIPLIER_EXCLUDED_IDS: ReadonlySet<AchievementId> =
  new Set<AchievementId>([
    "global-hov-gain-upgrade-milestone",
    "hov-9-gain-upgrade-milestone",
    "nhov-knows-nhov",
  ]);

export function contributesToAchievementCountMultiplier(
  id: AchievementId,
): boolean {
  return !ACHIEVEMENT_COUNT_MULTIPLIER_EXCLUDED_IDS.has(id);
}

const achievementEffects: Readonly<
  Partial<Record<AchievementId, AchievementEffectEntry>>
> = {
  "second-merge-hov": {
    persistent: { hovGainAchievementMultiplier: SECOND_MHOV_HG_MULTIPLIER },
  },
  "multi-branch-new-hov": {
    persistent: { hovGainAchievementMultiplier: NEW_NEW_HOV_HG_MULTIPLIER },
  },
  "hov-gain-family-upgrade": {
    persistent: {
      hovGainFamilyUpgradeRepeatInitialIntervalMs:
        HOV_GAIN_FAMILY_UPGRADE_UNLOCKED_REPEAT_INITIAL_INTERVAL_MS,
    },
  },
  "hov-gain-family-upgrade-marathon": {
    persistent: {
      hovGainFamilyUpgradeRepeatInitialIntervalMs:
        HOV_GAIN_FAMILY_UPGRADE_MARATHON_REPEATER_INITIAL_INTERVAL_MS,
    },
  },
  // EA scope: HovTim balance slider achievement is deferred.
  // "hov-hov": {
  //   persistent: { hovUpgradeCostMultiplier: 0.8 },
  // },
  "fourth-hov-gain": {
    persistent: { mpCapacityBonus: FOURTH_HOV_GAIN_MP_BONUS },
  },
  "cheap-fourth-hov-gain": {
    persistent: {
      hovUpgradeCostMultiplier: CHEAP_FOURTH_HOV_GAIN_COST_MULTIPLIER,
      hovCellUpgradeCostMultiplier: CHEAP_FOURTH_HOV_GAIN_COST_MULTIPLIER,
    },
  },
  "cheap-global-hov-gain-milestone": {
    persistent: {
      hovUpgradeCostMultiplier: CHEAP_GLOBAL_HOV_GAIN_MILESTONE_COST_MULTIPLIER,
      hovCellUpgradeCostMultiplier:
        CHEAP_GLOBAL_HOV_GAIN_MILESTONE_COST_MULTIPLIER,
    },
  },
  "not-fully-free": {
    persistent: {
      hovUpgradeCostMultiplier: NOT_FULLY_FREE_COST_MULTIPLIER,
      hovCellUpgradeCostMultiplier: NOT_FULLY_FREE_COST_MULTIPLIER,
    },
  },
  "ninth-hov-gain": {
    persistent: { mpCapacityBonus: NINTH_HOV_GAIN_MP_BONUS },
  },
  "mass-hov-gain-family-upgrade": {
    persistent: {
      canAssignMpToHovGainUpgrades: true,
      mpCapacityBonus: MASS_HOV_GAIN_FAMILY_UPGRADE_MP_BONUS,
    },
  },
  "music-pointer": {
    persistent: { mpCapacityBonus: MUSIC_POINTER_MP_BONUS },
  },
  "movie-pointer": {
    persistent: { mpCapacityBonus: MOVIE_POINTER_MP_BONUS },
  },
  "maschinen-pointer": {
    persistent: { mpCapacityBonus: MASCHINEN_POINTER_MP_BONUS },
  },
  "mhov-mp-execution-ten": {
    persistent: { mpCapacityBonus: MHOV_MP_EXECUTION_TEN_MP_BONUS },
  },
  "time-has-come": {
    persistent: {
      timHovActivationEfficiency:
        TIM_HOV_ACTIVATION_EFFICIENCY_AFTER_TIME_HAS_COME,
    },
  },
  "red-amplifier-full-charge": {
    persistent: {
      amplifierGainFixedMultipliersByEntry: new Map([
        ["red", huge(RED_AMPLIFIER_FULL_CHARGE_RAG_MULTIPLIER)],
      ]),
    },
  },
  google: {
    persistent: {
      bhgToHgPowerExponentBonus: GOOGLE_HG_POWER_EXPONENT_BONUS,
    },
  },
  "global-hov-gain-upgrade-tenth-milestone": {
    persistent: {
      bhgToHgPowerExponentBonus:
        GLOBAL_HOV_GAIN_UPGRADE_TENTH_MILESTONE_HG_POWER_EXPONENT_BONUS,
    },
  },
  "massive-merge-hov": {
    persistent: {
      bhgToHgPowerExponentBonus: MASSIVE_MERGE_HOV_HG_POWER_EXPONENT_BONUS,
    },
  },
  "rank-gap": {
    persistent: {
      bhgToHgPowerExponentBonus: RANK_GAP_HG_POWER_EXPONENT_BONUS,
    },
  },
  "tenth-outside-hov": {
    persistent: {
      bhgToHgPowerExponentBonus: TENTH_OUTSIDE_HOV_HG_POWER_EXPONENT_BONUS,
    },
  },
  "hov-1-gain-tries-too": {
    persistent: {
      h1gExponentBonus: HOV_1_GAIN_TRIES_TOO_H1G_EXPONENT_BONUS,
    },
  },
  "hov-1-gain-gets-serious": {
    persistent: {
      h1gExponentBonus: HOV_1_GAIN_GETS_SERIOUS_H1G_EXPONENT_BONUS,
    },
  },
  "one-cost-batch-upgrade": {
    persistent: {
      oneCostHoverRepeaterBatchEnabled: true,
    },
  },
  "burn-the-core": {
    persistent: {
      ohovHovCoreGainScalingUnlocked: true,
    },
  },
  "mp-wall-preset": {
    persistent: { mpmPresetSlotBonus: MP_WALL_PRESET_SLOT_BONUS },
  },
  "outside-mhov": {
    persistent: {
      mhovRewardUncapped: true,
    },
  },
  "outside-nhov": {
    persistent: {
      nhovBranchEffectUncapped: true,
    },
  },
  "amplifier-upgrade": {
    persistent: {
      amplifierUpgradeRepeatInitialIntervalMs:
        HOV_AMPLIFIER_UPGRADE_UNLOCKED_REPEAT_INITIAL_INTERVAL_MS,
      canAssignMpToAmplifierUpgrades: true,
    },
  },
  "exploration-tim-acceleration": {
    persistent: { explorationTimAccelerationUnlocked: true },
  },
  "exploration-biome-level-two": {
    persistent: { mpCapacityBonus: EXPLORATION_BIOME_LEVEL_MP_BONUS },
  },
  "x-pattern-hover": {
    oneTime: { tokenGrant: ACHIEVEMENT_SECRET_TOKEN_GRANT },
  },
  "hov-o-label": {
    oneTime: { tokenGrant: ACHIEVEMENT_SECRET_TOKEN_GRANT },
  },
  "h-pattern-unlock": {
    oneTime: { tokenGrant: ACHIEVEMENT_SECRET_TOKEN_GRANT },
  },
  "h-pattern-hover": {
    oneTime: { tokenGrant: ACHIEVEMENT_SECRET_TOKEN_GRANT },
  },
  "target-rapid-click": {
    oneTime: { tokenGrant: ACHIEVEMENT_SECRET_TOKEN_GRANT },
  },
};

const categoryById = new Map(
  achievementDefinitions.map((def) => [def.id, def.category] as const),
);

export interface ComputePersistentEffectsOptions {
  readonly hovGainCountMultiplierBase?: number;
  readonly timGainCountMultiplierBase?: number;
}

function safePow(base: number, count: number): number {
  if (!Number.isFinite(count) || count < 0) return 1;
  if (!Number.isFinite(base) || base <= 0) return 1;
  return Math.pow(base, count);
}

export function computeHovGainAchievementCountMultiplier(
  normalCount: number,
  base: number = HOV_GAIN_ACHIEVEMENT_COUNT_BASE_DEFAULT,
): number {
  return safePow(base, normalCount);
}

export function computeTimGainAchievementCountMultiplier(
  secretCount: number,
  base: number = TIM_GAIN_ACHIEVEMENT_COUNT_BASE_DEFAULT,
): number {
  return safePow(base, secretCount);
}

export function computePersistentEffects(
  unlockedIds: Iterable<AchievementId>,
  options: ComputePersistentEffectsOptions = {},
): AchievementPersistentEffects {
  let hovGainFlatBonus = 0;
  let hovUpgradeCostMultiplier = 1;
  let hovCellUpgradeCostMultiplier = 1;
  let hovGainFamilyUpgradeRepeatInitialIntervalMs =
    DEFAULT_UPGRADE_REPEAT_INITIAL_INTERVAL_MS;
  let amplifierUpgradeRepeatInitialIntervalMs =
    HOV_AMPLIFIER_UPGRADE_REPEAT_INITIAL_INTERVAL_MS;
  let hovGainAchievementMultiplier = 1;
  let mpCapacityBonus = 0;
  let mpmPresetSlotBonus = 0;
  let canAssignMpToHovGainUpgrades = false;
  let canAssignMpToAmplifierUpgrades = false;
  let timHovActivationEfficiency = TIM_HOV_ACTIVATION_EFFICIENCY_DEFAULT;
  let bhgToHgPowerExponentBonus = 0;
  let h1gExponentBonus = 0;
  let oneCostHoverRepeaterBatchEnabled = false;
  let ohovHovCoreGainScalingUnlocked = false;
  let mhovRewardUncapped = false;
  let nhovBranchEffectUncapped = false;
  let explorationTimAccelerationUnlocked = false;
  const amplifierGainFixedMultipliersByEntry = new Map<
    HovAmplifierEntryId,
    HugeNumber
  >();
  let normalCount = 0;
  let secretCount = 0;
  for (const id of unlockedIds) {
    if (contributesToAchievementCountMultiplier(id)) {
      if (categoryById.get(id) === "secret") {
        secretCount += 1;
      } else {
        normalCount += 1;
      }
    }
    const entry = achievementEffects[id];
    if (!entry?.persistent) continue;
    if (entry.persistent.hovGainFlatBonus !== undefined) {
      hovGainFlatBonus += entry.persistent.hovGainFlatBonus;
    }
    if (entry.persistent.hovUpgradeCostMultiplier !== undefined) {
      hovUpgradeCostMultiplier *= entry.persistent.hovUpgradeCostMultiplier;
    }
    if (entry.persistent.hovCellUpgradeCostMultiplier !== undefined) {
      hovCellUpgradeCostMultiplier *=
        entry.persistent.hovCellUpgradeCostMultiplier;
    }
    if (
      entry.persistent.hovGainFamilyUpgradeRepeatInitialIntervalMs !== undefined
    ) {
      hovGainFamilyUpgradeRepeatInitialIntervalMs = Math.min(
        hovGainFamilyUpgradeRepeatInitialIntervalMs,
        entry.persistent.hovGainFamilyUpgradeRepeatInitialIntervalMs,
      );
    }
    if (
      entry.persistent.amplifierUpgradeRepeatInitialIntervalMs !== undefined
    ) {
      amplifierUpgradeRepeatInitialIntervalMs = Math.min(
        amplifierUpgradeRepeatInitialIntervalMs,
        entry.persistent.amplifierUpgradeRepeatInitialIntervalMs,
      );
    }
    if (entry.persistent.hovGainAchievementMultiplier !== undefined) {
      hovGainAchievementMultiplier *=
        entry.persistent.hovGainAchievementMultiplier;
    }
    if (entry.persistent.mpCapacityBonus !== undefined) {
      mpCapacityBonus += entry.persistent.mpCapacityBonus;
    }
    if (entry.persistent.mpmPresetSlotBonus !== undefined) {
      mpmPresetSlotBonus += entry.persistent.mpmPresetSlotBonus;
    }
    if (entry.persistent.canAssignMpToHovGainUpgrades === true) {
      canAssignMpToHovGainUpgrades = true;
    }
    if (entry.persistent.canAssignMpToAmplifierUpgrades === true) {
      canAssignMpToAmplifierUpgrades = true;
    }
    if (entry.persistent.timHovActivationEfficiency !== undefined) {
      timHovActivationEfficiency *= entry.persistent.timHovActivationEfficiency;
    }
    if (entry.persistent.bhgToHgPowerExponentBonus !== undefined) {
      bhgToHgPowerExponentBonus += entry.persistent.bhgToHgPowerExponentBonus;
    }
    if (entry.persistent.h1gExponentBonus !== undefined) {
      h1gExponentBonus += entry.persistent.h1gExponentBonus;
    }
    if (entry.persistent.oneCostHoverRepeaterBatchEnabled === true) {
      oneCostHoverRepeaterBatchEnabled = true;
    }
    if (entry.persistent.ohovHovCoreGainScalingUnlocked === true) {
      ohovHovCoreGainScalingUnlocked = true;
    }
    if (entry.persistent.mhovRewardUncapped === true) {
      mhovRewardUncapped = true;
    }
    if (entry.persistent.nhovBranchEffectUncapped === true) {
      nhovBranchEffectUncapped = true;
    }
    if (entry.persistent.explorationTimAccelerationUnlocked === true) {
      explorationTimAccelerationUnlocked = true;
    }
    if (entry.persistent.amplifierGainFixedMultipliersByEntry !== undefined) {
      for (const [entryId, multiplier] of entry.persistent
        .amplifierGainFixedMultipliersByEntry) {
        const current = amplifierGainFixedMultipliersByEntry.get(entryId);
        amplifierGainFixedMultipliersByEntry.set(
          entryId,
          (current ?? hugeOne()).mul(multiplier),
        );
      }
    }
  }
  return {
    hovGainFlatBonus,
    hovUpgradeCostMultiplier,
    hovCellUpgradeCostMultiplier,
    hovGainFamilyUpgradeRepeatInitialIntervalMs,
    amplifierUpgradeRepeatInitialIntervalMs,
    hovGainAchievementCountMultiplier: computeHovGainAchievementCountMultiplier(
      normalCount,
      options.hovGainCountMultiplierBase,
    ),
    timGainAchievementCountMultiplier: computeTimGainAchievementCountMultiplier(
      secretCount,
      options.timGainCountMultiplierBase,
    ),
    hovGainAchievementMultiplier,
    mpCapacityBonus,
    mpmPresetSlotBonus,
    canAssignMpToHovGainUpgrades,
    canAssignMpToAmplifierUpgrades,
    timHovActivationEfficiency,
    bhgToHgPowerExponentBonus,
    h1gExponentBonus,
    oneCostHoverRepeaterBatchEnabled,
    ohovHovCoreGainScalingUnlocked,
    mhovRewardUncapped,
    nhovBranchEffectUncapped,
    explorationTimAccelerationUnlocked,
    amplifierGainFixedMultipliersByEntry,
  };
}

export function getOneTimeEffect(
  id: AchievementId,
): AchievementOneTimeEffect | null {
  return achievementEffects[id]?.oneTime ?? null;
}
